import { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@features/auth/store/authStore';
import { useProactiveRefresh } from '@features/auth/hooks/useProactiveRefresh';
import { applyTenantBrandColors } from '@lib/utils/colorScale';
import { SupportErrorProvider } from '@features/support';
import { AppRoutes } from './routes';

function AppInner() {
  const queryClient = useQueryClient();
  const { isAuthenticated, clearAuth, tenantInfo } = useAuthStore();

  // Proactively refresh the JWT access token before it expires so
  // users never hit a 401 during normal usage. Also handles tab
  // wake-up after laptop sleep.
  useProactiveRefresh();

  // Aplica los colores de marca del tenant activo (login previo a autenticar,
  // o ya persistidos en el store tras login) a las variables CSS globales.
  // Sin esto, primaryColor/secondaryColor se traían del BFF pero nunca se
  // reflejaban visualmente en ningún lado.
  useEffect(() => {
    applyTenantBrandColors(tenantInfo?.primaryColor, tenantInfo?.secondaryColor);
  }, [tenantInfo?.primaryColor, tenantInfo?.secondaryColor]);

  // Sync logout across browser tabs via StorageEvent — but ONLY when the
  // session that was cleared is THIS tab's own session. localStorage is
  // shared by all tabs, so if the same user has a duplicate session open
  // elsewhere (a different tab/login with its own sessionId) and logs out
  // there, 'accessToken'/'sessionId' get removed from the shared storage
  // too — without this check that would force-logout THIS tab even though
  // its own session is still perfectly valid on the backend.
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (
        e.key === 'sessionId' &&
        e.newValue === null &&
        isAuthenticated &&
        e.oldValue === useAuthStore.getState().sessionId
      ) {
        clearAuth();
        queryClient.clear();
        window.location.href = '/login';
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [isAuthenticated, clearAuth, queryClient]);

  // El provider del asistente envuelve las rutas: expone `useReportError()` a
  // cualquier pantalla y sostiene el modal de error, el recorrido guiado y el
  // toast de feedback fuera del arbol de cada feature (asi sobreviven a que la
  // pantalla que los disparo se desmonte). Si el agente no esta configurado en
  // el BFF, no monta nada.
  return (
    <SupportErrorProvider>
      <AppRoutes />
    </SupportErrorProvider>
  );
}

function App() {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <AppInner />
    </BrowserRouter>
  );
}

export default App;
